/* eslint-disable react-refresh/only-export-components */
import { gpay, master, momo, paypal, visa } from "./logos"

import { Box, Button, CircularProgress, Grid, Stack, TextField, Typography } from "@mui/material"
import { grey } from "@mui/material/colors"

import GPayWO from "./GPayWO.svg"
import PaypalWO from "./PaypalWO.svg"

import { client } from "braintree-web/index"
import { useEffect, useRef, useState } from "react"

import { vestResolver } from "@hookform/resolvers/vest"
import { Controller, useForm } from "react-hook-form"
import { getPaymentClientToken } from "../../../../../services/payment"
import validators from "./validators"

const braintree = window.braintree as any

const validateStyles = {
  border: '1px solid rgba(0, 0, 0, 0.23)',
  p: 2
}

export function Card({ provider, callback, validate = true, ...props }: any) {
  const [token, setToken] = useState<string | null>(null)
  const [state, setState] = useState<any>(props.state || null)
  const [isLoading, setLoading] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    formState: {
      errors
    }
  } = useForm({
    defaultValues: {
      number: "",
      cardholderName: "",
      expirationDate: "",
      cvv: ""
    },
    resolver: vestResolver(validators)
  });

  // const sample = {
  //   number: 5555555555554444,
  //   cardholderName: 'NGUYEN KIM THO',
  //   cvv: 845,
  //   expirationDate: '08/30',
  //   billingAddress: {
  //     postalCode: 700000
  //   },
  // }

  async function resolvePaymentClienToken() {
    const response = await getPaymentClientToken(provider)

    const { token } = response?.data || {}

    if (token) {
      setToken(token)
    }
  }

  function handleResult(err: any, result: any) {
    if (err) {
      return console.log(err)
    }

    if (result) {
      // console.log(result)

      const { nonce, details } = result?.creditCards?.[0] || {}

      return [
        setState({
          nonce,
          details
        }),
        setLoading(false),
        callback({
          nonce,
          provider: details?.cardType
        })
      ]
    }
  }

  async function handleSubmitCardInformation(data: any) {
    if (token) {
      setLoading(true)

      const instance = await client.create({
        authorization: token
      })

      if (instance) {
        instance.request({
          endpoint: 'payment_methods/credit_cards',
          method: 'post',
          data: {
            creditCard: {
              ...data,
              options: {
                validate: false
              }
            }
          }
        }, handleResult)
      }
    }
  }

  useEffect(() => {
    resolvePaymentClienToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box sx={{ mt: 1, maxWidth: 400 }}>
      {state ? (
        <>
          <Box sx={{ ...(validate ? validateStyles : {}), borderRadius: 1 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography fontSize={14} fontWeight={700}>{state?.details?.cardholderName}</Typography>
              <Typography fontSize={14} fontWeight={700} color={grey[600]}>{state?.details?.cardType} *** {state?.details?.lastFour}</Typography>
            </Stack>
            <Typography fontSize={14}>{state?.details?.expirationMonth}/{state?.details?.expirationYear}</Typography>
          </Box>
          {validate && <Typography
            fontSize={13}
            sx={{
              color: 'primary',
              textAlign: 'right',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
            onClick={() => setState(null)}
          >Change Card</Typography>}
        </>
      ) : (
        <Box component="form" onSubmit={handleSubmit(handleSubmitCardInformation)} sx={{ mt: 2, maxWidth: 400 }}>
          <Controller
            control={control}
            name="number"
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                label="Card Number (*)"
                error={!!errors.number}
                InputLabelProps={{ shrink: true }}
                helperText={errors.number?.message as any}
                fullWidth
                sx={{ "& input": { fontSize: 13 }, mt: 2 }}
              />
            )}
          />

          <Controller
            control={control}
            name="cardholderName"
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                label="Card Holder (*)"
                error={!!errors.cardholderName}
                InputLabelProps={{ shrink: true }}
                helperText={errors.cardholderName?.message as any}
                fullWidth
                sx={{ "& input": { fontSize: 13 }, mt: 2 }}
              />
            )}
          />

          <Grid container>
            <Grid item xs={8}>
              <Controller
                control={control}
                name="expirationDate"
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    type="text"
                    label="Expires Date (*)"
                    error={!!errors.expirationDate}
                    InputLabelProps={{ shrink: true }}
                    helperText={errors.expirationDate?.message as any}
                    fullWidth
                    sx={{ "& input": { fontSize: 13 }, mt: 2 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                control={control}
                name="cvv"
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    type="text"
                    label="CVV (*)"
                    error={!!errors.cvv}
                    InputLabelProps={{ shrink: true }}
                    helperText={errors.cvv?.message as any}
                    fullWidth
                    sx={{ "& input": { fontSize: 13 }, mt: 2 }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Button
            disabled={isLoading}
            type="submit"
            sx={{ mt: 3 }}
            fullWidth
            variant="contained"
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: grey[900] }} />
            ) : (
              <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize' }}>Verify</Typography>
            )}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export function GooglePay({ provider, callback, validate = true, ...props }: any) {
  const [token, setToken] = useState<string | null>(null)
  const [state, setState] = useState<any>(props.state || null)
  const [isLoading, setLoading] = useState<boolean>(false)

  async function resolvePaymentClienToken() {
    const response = await getPaymentClientToken(provider)

    const { token } = response?.data || {}

    if (token) {
      setToken(token)
    }
  }

  function handleResult(err: any, result: any) {
    if (err) {
      return console.log(err)
    }

    if (result) {
      // console.log(result)

      const { nonce } = result

      return [
        setState({
          ...result,
          cardType: result.type
        }),
        callback({
          nonce,
          provider
        })
      ]
    }
  }

  async function handleInitialGooglePay(err: any, instance: any) {
    if (err) {
      return console.log(err)
    }

    if (instance) {
      const gpay = new google.payments.api.PaymentsClient({
        environment: 'TEST'
      });

      const paymentDataRequest = instance.createPaymentDataRequest({
        transactionInfo: {
          currencyCode: 'USD',
          totalPriceStatus: 'FINAL',
          totalPrice: '1.00'
        }
      });

      const data = await gpay.loadPaymentData(paymentDataRequest)

      instance.parseResponse(data, handleResult)
    }
  }

  async function handleAuthenticateWithGooglePay() {
    if (token) {
      setLoading(true)

      const instance = await client.create({
        authorization: token
      })

      if (instance) {
        setLoading(false)

        braintree.googlePayment.create({
          client: instance,
          googlePayVersion: 2,
          googleMerchantId: 'sandbox'
        }, handleInitialGooglePay);
      }
    }
  }

  useEffect(() => {
    resolvePaymentClienToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Box sx={{ mt: 2, maxWidth: 400 }}>
        {state ? (
          <>
            <Box sx={{ ...(validate ? validateStyles : {}), borderRadius: 1 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography fontSize={14} fontWeight={700}>{state?.type}</Typography>
                <Typography fontSize={14} fontWeight={700} color={grey[600]}>{state?.details?.cardType} *** {state?.details?.lastFour}</Typography>
              </Stack>
            </Box>
            {validate && <Typography
              fontSize={13}
              sx={{
                color: 'primary',
                textAlign: 'right',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              onClick={() => handleAuthenticateWithGooglePay()}
            >Change Card</Typography>}
          </>
        ) : (
          <Button
            disabled={isLoading}
            type="submit"
            fullWidth
            onClick={() => handleAuthenticateWithGooglePay()}
            sx={{ bgcolor: grey[50], color: 'black ! important' }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: grey[900] }} />
            ) : (
              <>
                <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize', mr: 1 }}>Authenticate with</Typography>
                <img src={GPayWO} style={{ width: 'auto', height: '16px' }} />
              </>
            )}
          </Button>
        )}
      </Box>
    </>
  )
}

export function Paypal({ provider, callback, validate = true, ...props }: any) {
  const container = useRef<any>(null)
  const [state, setState] = useState<any>(props.state || null)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [token, setToken] = useState<string | null>(null)

  function handleResult(err: any, result: any) {
    if (err) {
      return console.log(err)
    }

    if (result) {
      // console.log(result)

      const { nonce } = result

      return [
        setState(result),
        callback({
          nonce,
          provider
        })
      ]
    }
  }

  async function handleAuthenticateWithPaypal() {
    if (token) {
      setLoading(true)

      const instance = await braintree.client.create({
        authorization: token
      })

      if (instance) {
        // console.log(instance)

        braintree.paypalCheckout.create({
          client: instance
        }, function (err: any, paypalCheckoutInstance: any) {
          if (err) {
            return console.log(err)
          }

          if (paypalCheckoutInstance) {
            setLoading(false)

            paypalCheckoutInstance.loadPayPalSDK({
              intent: 'capture',
              currency: 'USD',
              commit: true,
              vault: true,
            }, function () {
              window.paypal.Buttons({
                fundingSource: window.paypal.FUNDING.PAYPAL as any,

                createBillingAgreement: function () {
                  return paypalCheckoutInstance.createPayment({
                    flow: 'vault'
                  });
                },

                onApprove: function (data) {
                  return paypalCheckoutInstance.tokenizePayment(data, handleResult)
                }
              }).render(container.current);
            });
          }
        })
      }
    }
  }

  async function resolvePaymentClienToken() {
    const response = await getPaymentClientToken(provider)

    const { token } = response?.data || {}

    if (token) {
      setToken(token)
    }
  }

  useEffect(() => {
    resolvePaymentClienToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Box sx={{ mt: 2, maxWidth: 400 }}>
        {state ? (
          <>
            <Box sx={{ ...(validate ? validateStyles : {}), borderRadius: 1 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography fontSize={14} fontWeight={700}>{state?.type}</Typography>
                <Typography fontSize={14} fontWeight={700} color={grey[600]}>{state?.details?.tenant} *** {state?.details?.payerId}</Typography>
              </Stack>
              <Typography fontSize={14}>{state?.details?.email}</Typography>
            </Box>
            {validate && <Typography
              fontSize={13}
              sx={{
                color: 'primary',
                textAlign: 'right',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              onClick={() => [setState(null), handleAuthenticateWithPaypal()]}
            >Change</Typography>}
          </>
        ) : (
          <>
            <div ref={container} />
            <Button
              disabled={isLoading}
              type="submit"
              fullWidth
              onClick={() => handleAuthenticateWithPaypal()}
              sx={{ bgcolor: grey[50], color: 'black ! important' }}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: grey[900] }} />
              ) : (
                <>
                  <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize', mr: 1 }}>Authenticate with</Typography>
                  <img src={PaypalWO} style={{ width: 'auto', height: '16px' }} />
                </>
              )}
            </Button>
          </>
        )}
      </Box>
    </>
  )
}

export default function PaymentMethod({ url, width = 50, height = 35, onClick, active = false }: any) {
  return (
    <Box sx={{
      width,
      height,
      background: `url(${url})`,
      backgroundPosition: 'center',
      backgroundSize: '100%',
      backgroundRepeat: 'no-repeat',
      borderRadius: 1,
      cursor: 'pointer',
      "&:hover": {
        bgcolor: grey[100]
      },
      bgcolor: active ? grey[200] : undefined
    }} onClick={onClick} />
  )
}

export {
  gpay,
  master,
  momo,
  paypal,
  visa
}
